import { refreshPaymentMethod, setupBillingCustomer } from '@/api/cloudFunctionWrappers'
import { getCurrentUserIdToken } from '@/services/auth'
import { generateDriveName, generateWorkstationName } from '@/services/names'
import { PaymentMethodUpdateInfo, ResourceSetupData } from '@/store/types/user'
import { Codename } from './types/workstation'
// requests
import { errorIfInvalidResponse } from './errors'
import {
  composedFetch,
  deleteWorkstationURL,
  getPublicBundlesURL,
  getPublicWorkstationsURL,
  getResourcesURL,
  getShareDrivesURL,
} from './helpers'
import { Billing } from './types/user'

// PAYMENT

export const apiUpdatePayment = async (
  stripeObject: PaymentMethodUpdateInfo,
  billing: Billing,
  email: string,
): Promise<void> => {
  const { stripe, cardholderName, card } = stripeObject
  const {
    stripe_customer_identifier,
    stripe_active_payment_method_identifier,
    full_name,
    address_line_1,
    address_line_2,
    city,
    postal_code,
    country,
    card: currentCard,
    affiliate_hash,
  } = billing

  let extraFields = {}
  if (currentCard) {
    extraFields = {
      last_4_digits: currentCard.last_4_digits,
      cardholder_name: currentCard.cardholder_name,
      exp_month: currentCard.exp_month,
      exp_year: currentCard.exp_year,
    }
  }
  const setupResult = await setupBillingCustomer({
    billing: {
      full_name,
      address_line_1: address_line_1,
      address_line_2: address_line_2,
      postal_code,
      city,
      country,
      stripe_customer_identifier,
      stripe_active_payment_method_identifier,
      card: {
        ...currentCard,
        ...extraFields,
      },
      affiliate_hash,
    },
  })

  const clientSecret = setupResult.data.clientSecret
  const result = await stripe.confirmCardSetup(clientSecret, {
    payment_method: {
      card,
      billing_details: {
        email: email,
        name: cardholderName,
        address: {
          line1: address_line_1,
          line2: address_line_2,
          country: country,
          city: city,
          postal_code,
        },
      },
    },
  })
  if (result.error?.message) {
    throw new Error(result.error?.message)
  } else if (!result.setupIntent?.payment_method) {
    throw new Error('Unable to save payment method')
  }

  await refreshPaymentMethod(result.setupIntent.payment_method as string)
}

// TEAMS

/**
 * Gets a list of uuids of drives shared with a given user, handles errors if necessary.
 * @returns the uuid list
 */
export async function apiGetShareeDrivesUUIDs(): Promise<string[]> {
  const response = await composedFetch('GET', getShareDrivesURL(), await getCurrentUserIdToken())
  await errorIfInvalidResponse(response)
  return await response.json()
}

// "I need this fetch with different body" helper
export async function postResourcesCreation(bodyObj?: ResourceSetupData) {
  return composedFetch('POST', getResourcesURL(), await getCurrentUserIdToken(), bodyObj)
}

type BatchCreateOptions = {
  driveOnly?: boolean // by default it creates a pair, with this it goes only with drive
  workstationCodename: Codename
}
//  ONBOARDING
/**
 * Converts argument object into shape required by the API, calls helper for a hit, handles errors or returns value.
 * @returns (not sure if necessary) the connector success response.
 */
export const apiSetupOnboardingResources = async ({
  driveOnly,
  workstationCodename,
}: BatchCreateOptions): Promise<void> => {
  // type SetupResource = { name: string }
  const setupInfoObject: ResourceSetupData = {
    drives: [{ name: generateDriveName() }],
    workstations: [],
  }

  if (!driveOnly) {
    setupInfoObject.workstations.push({
      name: generateWorkstationName(),
      codename: workstationCodename,
    })
  }

  // actual endpoint hit
  const response = await postResourcesCreation(setupInfoObject)
  await errorIfInvalidResponse(response)
  return await response.json()
}

// DRIVE

/**
 * Converts argument object into shape required by the API, calls helper for a hit, handles errors or returns value.
 * @param name drive name
 * @returns (not sure if necessary) the connector success response.
 */
export const apiCreateSingleDrive = async (name: string): Promise<void> => {
  if (!name) {
    throw new Error('Provide name to create a drive.')
  }

  const setupInfoObject = {
    drives: [
      {
        name,
      },
    ],
    workstations: [],
  }
  // actual endpoint hit
  const response = await postResourcesCreation(setupInfoObject)
  await errorIfInvalidResponse(response)
  return await response.json()
}

// WORKSTATION

export const deleteWorkstation = async (workstationUuid: string): Promise<any> => {
  const response = await composedFetch(
    'DELETE',
    deleteWorkstationURL(workstationUuid),
    await getCurrentUserIdToken(),
  )

  await errorIfInvalidResponse(response, 'Failed to delete workstation')
  return 'success'
}

/**
 * Get available bundles
 * @returns an Array of available bundles, d'uh
 */
export const apiGetPublicBundles = async (): Promise<any> => {
  const response = await composedFetch('GET', getPublicBundlesURL(), await getCurrentUserIdToken())
  await errorIfInvalidResponse(response)
  return await response.json()
}

export const apiGetPublicWorkstations = async (): Promise<any> => {
  const response = await composedFetch(
    'GET',
    getPublicWorkstationsURL(),
    await getCurrentUserIdToken(),
  )
  await errorIfInvalidResponse(response)

  return await response.json()
}

// ADMIN

// for easy access to current user idToken in admin panel to use it in e.g. in postman requests
export const apiGetIdToken = async (): Promise<any> => {
  const response = await getCurrentUserIdToken().then(async (idToken) => {
    return idToken
  })
  return response
}
